import type { KeyboardEvent } from "react";

import { AlertTriangle } from "@untitledui/icons";

import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

import {
	formatLocationSetupGapsTooltip,
	hasStructuralSetupGaps,
	type LocationSetupGap,
} from "../../utils/location/location-setup-gaps";
import {
	buildClusterStatusSegments,
	type ClusterSensorStatusCounts,
} from "./aggregate-status";

interface ClusterPillMarkerProps extends ClusterSensorStatusCounts {
	count: number;
	dimmed?: boolean;
	interactive?: boolean;
	isHovered?: boolean;
	/** Setup gaps for location clusters (installation / zone / sensors). */
	missingSetup?: LocationSetupGap[];
	name: string;
	onClick?: () => void;
}

/**
 * Dark card used for a location on the Map Configuration overview.
 * White badge = mapped sensor count; subtitle = sensor health rollup.
 *
 * Rendered as `role="button"` (not `<button>`) so the setup-gap tooltip
 * trigger can remain a real button without invalid nesting.
 */
export const ClusterPillMarker = ({
	count,
	dimmed = false,
	interactive = true,
	isHovered = false,
	missingSetup = [],
	name,
	offline,
	onClick,
	online,
	warning,
}: ClusterPillMarkerProps) => {
	const segments = buildClusterStatusSegments({
		missingSetup,
		offline,
		online,
		warning,
	});
	const showSetupAlert = hasStructuralSetupGaps(missingSetup);
	const missingTooltip = showSetupAlert
		? formatLocationSetupGapsTooltip(missingSetup)
		: "";

	const handleActivate = () => {
		if (!interactive) return;
		onClick?.();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (!interactive) return;
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			event.stopPropagation();
			onClick?.();
		}
	};

	return (
		<div
			aria-disabled={interactive ? undefined : true}
			className={cx(
				"map-cluster-card relative flex max-w-56 min-w-40 flex-col gap-1 rounded-xl border-2 border-brand px-3 py-2.5 text-left shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
				"bg-primary-solid",
				interactive ? "cursor-pointer" : "cursor-default",
				dimmed && "opacity-60 saturate-50",
				isHovered && "map-cluster-card-hover",
			)}
			onClick={
				interactive
					? (event) => {
							event.stopPropagation();
							handleActivate();
						}
					: undefined
			}
			onKeyDown={interactive ? handleKeyDown : undefined}
			role="button"
			tabIndex={interactive ? 0 : -1}
		>
			<div className="relative z-10 flex items-start justify-between gap-3">
				<span className="truncate text-sm leading-tight font-semibold text-white">
					{name}
				</span>
				<span className="flex h-5 min-w-6 shrink-0 items-center justify-center rounded-md bg-white px-1.5 text-xs font-semibold text-black tabular-nums">
					{count > 99 ? "99+" : count}
				</span>
			</div>
			{segments.length > 0 || missingTooltip ? (
				<span className="relative z-10 flex flex-wrap items-center gap-x-1.5 text-xs leading-tight font-medium">
					{missingTooltip ? (
						<Tooltip placement="top" title={missingTooltip}>
							<TooltipTrigger
								aria-label={missingTooltip}
								className="inline-flex shrink-0"
								onClick={(event) => {
									event.stopPropagation();
								}}
							>
								<AlertTriangle className="size-3.5 shrink-0 text-utility-warning-500" />
							</TooltipTrigger>
						</Tooltip>
					) : null}
					{segments.map((segment) => (
						<span className={segment.className} key={segment.kind}>
							{segment.text}
						</span>
					))}
				</span>
			) : null}
		</div>
	);
};
